import {FilterSearchMenu} from '@/components/filters/FilterSearchMenu/FilterSearchMenu';
import {PageHeader} from '@/layout/Page/PageHeader';
import {OverviewField, OverviewSection} from '@/layout/TabLayout';
import {SearchInput} from '@ark/web';
import {
  AvatarTextPattern,
  AvatarTextPatternSkeleton,
  ListItemActions,
  ListItemColumn,
  ListItemHeaderActions,
  ListItemSkeleton,
  StyledListItem,
  TextColumnPatternSkeleton,
} from '@/components/styled/ListItem';
import {COMMUNITIES_COLUMNS} from '@/pages/Admin/Teams/Tabs/CommunitiesTab/communitiesColumns';
import {CommunitiesListHeader} from '@/pages/Admin/Teams/Tabs/CommunitiesTab/CommunitiesListHeader';
import {fullImageUrl} from '@/utils/files';
import {Delete, Info, Lock} from '@mui/icons-material';
import {Box, IconButton, ListItemText, Typography} from '@mui/material';
import {RuleCommunityFragment} from '@/generated/graphql';
import {colors} from '@ark/react';
import {useMemo, useState} from 'react';

type NewRuleOverviewTabProps = {
  allCommunities: RuleCommunityFragment[];
  selectedCommunityIds: string[];
  onCommunityToggle: (communityId: string, checked: boolean) => void;
  loading: boolean;
};

export const NewRuleOverviewTab = ({
  allCommunities,
  selectedCommunityIds,
  onCommunityToggle,
  loading,
}: NewRuleOverviewTabProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const communityOptions = useMemo(() => {
    return allCommunities.map(c => ({
      value: c.id,
      label: c.name || '',
      checked: selectedCommunityIds.includes(c.id),
    }));
  }, [allCommunities, selectedCommunityIds]);

  const selectedCommunityObjects = useMemo(() => {
    const filtered = allCommunities.filter(c =>
      selectedCommunityIds.includes(c.id),
    );
    if (!searchTerm) return filtered;
    return filtered.filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [allCommunities, selectedCommunityIds, searchTerm]);

  const skeletonCount = selectedCommunityIds.length || 3;

  return (
    <OverviewSection>
      <OverviewField label="Categories" variant="inline">
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Lock sx={{fontSize: 14, color: 'text.disabled'}} />
          <Typography
            component="span"
            sx={{color: 'text.disabled', fontSize: 'inherit'}}
          >
            No categories
          </Typography>
        </Box>
      </OverviewField>
      <OverviewField label="Communities">
        <PageHeader
          innerBoxSx={{
            paddingLeft: 0,
            paddingRight: 0,
          }}
          headerSx={{
            top: 32,
          }}
          searchComponent={
            <SearchInput
              placeholder="Search communities..."
              onSearchChange={setSearchTerm}
            />
          }
          filterComponents={[
            <FilterSearchMenu
              key="filter-menu"
              options={communityOptions}
              buttonLabel="Add Communities"
              searchPlaceholder="Search communities..."
              onFilterChange={onCommunityToggle}
              selectAll={false}
              controlRowLabel="Name"
              anchorOrigin={{vertical: 'top', horizontal: 'right'}}
              transformOrigin={{vertical: 'top', horizontal: 'left'}}
              paperSx={{marginLeft: '8px', marginBottom: '8px'}}
            />,
          ]}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CommunitiesListHeader />
          {loading &&
            Array.from({
              length: skeletonCount < 10 ? skeletonCount : 10,
            }).map((_, index) => (
              <ListItemSkeleton key={index} variant="communities" size="md">
                <AvatarTextPatternSkeleton
                  flexBasis={COMMUNITIES_COLUMNS[0].flexBasis}
                  minWidth={COMMUNITIES_COLUMNS[0].minWidth}
                  avatarSize={20}
                />
                <TextColumnPatternSkeleton
                  flexBasis={COMMUNITIES_COLUMNS[1].flexBasis}
                  minWidth={COMMUNITIES_COLUMNS[1].minWidth}
                />
                <TextColumnPatternSkeleton
                  flexBasis={COMMUNITIES_COLUMNS[2].flexBasis}
                  minWidth={COMMUNITIES_COLUMNS[2].minWidth}
                />
                <ListItemHeaderActions width={40} />
              </ListItemSkeleton>
            ))}
          {!loading &&
            selectedCommunityObjects.map(community => (
              <StyledListItem
                key={community.id}
                variant="communities"
                size="md"
                cursor="default"
                data-list-item
              >
                <AvatarTextPattern
                  avatar={{
                    src: community.avatar
                      ? fullImageUrl(community.avatar)
                      : undefined,
                    fallback: community.name?.[0] || '',
                    color: colors.black,
                    size: 20,
                  }}
                  primary={community.name}
                  flexBasis={COMMUNITIES_COLUMNS[0].flexBasis}
                  minWidth={COMMUNITIES_COLUMNS[0].minWidth}
                />
                <ListItemColumn
                  flexBasis={COMMUNITIES_COLUMNS[1].flexBasis}
                  minWidth={COMMUNITIES_COLUMNS[1].minWidth}
                >
                  <ListItemText>{community.homeCount}</ListItemText>
                </ListItemColumn>
                <ListItemColumn
                  flexBasis={COMMUNITIES_COLUMNS[2].flexBasis}
                  minWidth={COMMUNITIES_COLUMNS[2].minWidth}
                >
                  <ListItemText>{community.residentCount}</ListItemText>
                </ListItemColumn>
                <ListItemActions width={40}>
                  <IconButton
                    size="small"
                    onClick={() => onCommunityToggle(community.id, false)}
                  >
                    <Delete
                      fontSize="small"
                      sx={{color: theme => theme.palette.grey[700]}}
                    />
                  </IconButton>
                </ListItemActions>
              </StyledListItem>
            ))}
        </Box>
      </OverviewField>
      {selectedCommunityIds.length === 0 && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: 'action.hover',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Info sx={{fontSize: 20, color: 'text.secondary'}} />
          <Typography variant="body2" sx={{color: 'text.secondary'}}>
            Select at least one community
          </Typography>
        </Box>
      )}
    </OverviewSection>
  );
};
